import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { WebStorage } from '../storage/web.storage';
import { Common } from '../../../utils/common';

@Injectable({
    providedIn: 'root',
})
export class PermissionGuard {
    constructor(
        private router: Router,
        private storage: WebStorage
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        const user = this.storage.getUserData();
        if (!user) {
            return this.router.createUrlTree(['/login']);
        }

        // Admin has full access
        // if (Common.isAdminUserLogin(this.storage)) {
        //     return true;
        // }

        const url = state.url;

        // Check permissions based on URL patterns
        if (this.hasRequiredPermissions(url)) {
            
            return true;
        }

        // If no permissions match, redirect to not found
        return this.router.createUrlTree(['/notfound']);
    }

    private hasRequiredPermissions(url: string): boolean {
        // Staff routes (general)
        // Staff overview and list - Admin only
        if (url === '/staff' || url.includes('/staff/overview') || url.includes('/staff-list')) {
            return Common.isAdminUserLogin(this.storage);
        }
    
        // Staff attendance view - Admin only
        if (url.includes('/staff/viewAttendance/daily-view')) {
            return Common.isAdminUserLogin(this.storage);
        }
    
        // Other staff routes - require staff permissions
        if (url.startsWith('/staff/')) {
            return this.storage.hasAnyAuthority([
                'Staff Profile (Full Access)',
                'Staff Profile (Read)',
                'Staff (Full Access)',
                'Staff (Read)'
            ]);
        }

        // Student routes
        if (url.includes('/myschool/student') || url.includes('/student-Sign-In')) {
            return this.storage.hasAnyAuthority([
                'Student Profile (Full Access)',
                'Student Profile (Read)',
                'Student (Full Access)',
                'Student (Read)'
            ]);
        }

        // Room/Classroom routes
        if (url.includes('/myschool/room') || url.includes('/room')) {
            return this.storage.hasAnyAuthority([
                'Room Profile (Read)',
                'Room Profile (Full Access)'
            ]);
        }

        // Attendance routes
        if (url.includes('/attendance') || url.includes('dailyAttendance') ||
            url.includes('viewAttendance')) {
            return this.storage.hasAnyAuthority([
                'Attendance Daily (Full Access)',
                'Attendance Daily (Read)',
                'Attendance Monthly (Read)',
                'Attendance Absent (Full Access)',
                'Attendance Absent (Read)',
                'Staff Attendance (Full Access)'
            ]);
        }

        // Daily Activity routes
        if (url.includes('/parentConnection/activities') ||
            url.includes('/activities/all')) {
            return this.storage.hasAnyAuthority([
                'Parent Connection Newsletter (Full Access)',
                'Parent Connection Newsletter (Read)',
                'Parent Connection Daily Activity (Read)',
                'Parent Connection Daily Activity (Full Access)',
                'Parent Connection Daily Activity Feed (Read)',
                'Parent Connection Daily Activity Feed (Full Access)'
            ]);
        }

        // Communication routes (general)
        if (url.includes('/parentConnection') || url.includes('/communication')) {
            return this.storage.hasAnyAuthority([
                'Parent Connection Newsletter (Full Access)',
                'Parent Connection Newsletter (Read)',
                'Parent Connection Message (Full Access)',
                'Prent Connection Message (Read)',
                'Parent Connection Daily Activity (Read)',
                'Parent Connection Daily Activity (Full Access)',
                'Parent Connection Daily Activity Feed (Full Access)'
            ]);
        }

        // Scheduling routes - granular permissions
        if (url.includes('/scheduling/classroom-view')) {
            // Classroom view requires full access (admin only)
            return this.storage.hasAnyAuthority([
                'Staff Scheduling (Full Access)'
            ]);
        }

        if (url.includes('/scheduling/staff-view')) {
            // Staff view can be accessed by staff with read or full access
            return this.storage.hasAnyAuthority([
                'Staff Scheduling (Full Access)',
                'Staff Scheduling (Read)'
            ]);
        }

        if (url.includes('/scheduling')) {
            // General scheduling access
            return this.storage.hasAnyAuthority([
                'Staff Scheduling (Full Access)',
                'Staff Scheduling (Read)'
            ]);
        }

        // Billing routes
        // Billing routes - granular permissions
        if (url.includes('/billing/accounting/family-transaction') ||
            url.includes('/billings/accounting/family-transaction') ||
            url.includes('/billing/accounting/familylayout/family-transaction') ||
            url.includes('/billing/accounting/familylayout/statement') ||
            url.includes('/billings/accounting/familylayout/family-transaction')) {                        
            // Family transaction - allow parent access with ownership validation
            return this.storage.hasAnyAuthority([
                'Billing Accounting (Full Access)',
                'Billing Accounting (Read)',
                'Billing Recurring Active Plans (Full Access)',
                'Billing Recurring Active Plans (Read)',
                'Billing Recurring Plan Templates (Full Access)',
                'Billing Recurring Plan Templates (Read)',
                'All Transaction List (Full Access)',
                'All Transaction List (Read)'
            ]);
        }

        if (url.includes('/billing')) {
            // All other billing routes - Admin only
            return this.storage.hasAnyAuthority([
                'Billing Accounting (Full Access)'
            ]);
        }

        // Reports routes
        if (url.includes('/reports')) {
            return this.storage.hasAnyAuthority([
                'Reports (Full Access)',
                'Reports (Read)'
            ]);
        }

        // Other Future Enrollment routes - Admin or specific permissions
        if ((url.includes('/future-enrollment') && !url.includes('/room-movement'))
            || url.includes('/availability')
            || url.includes('/future-availability')
        ) {
            // Check if user has admin role OR required feature permissions
            const isAdminRole = Common.isAdminUserLogin(this.storage);
            const hasRequiredPermissions = this.storage.hasAnyAuthority([
                'Room Move (Full Access)',
                'Enrollment (Full Access)',
                'Availability (Read)'
            ]);

            // Allow access if user is admin OR has required permissions
            return isAdminRole || hasRequiredPermissions;
        }


        // Settings routes
        if (url.includes('/settings') || url.includes('/gen-settings')) {
            return this.storage.hasAnyAuthority([
                'School Settings General (Edit)',
                'School Settings Immunization (Full Access)',
                'School Settings Immunization (Read)',
                'School Settings Custom Fields (Full Access)',
                'School Settings Custom Fields (Read)',
                'Staff (Full Access)'
            ]);
        }

        // Role Permission routes
        if (url.includes('/rolePermission') || url.includes('/role-permission')) {
            // This is typically admin only, but check if user has access
            return this.storage.hasAnyAuthority([
                'Staff (Full Access)',
                'School Settings General (Edit)'
            ]);
        }

        // For routes that don't have specific permission requirements,
        // allow access (like dashboard, landing, etc.)
        return true;
    }
}